import { createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ContractPricingFrequency } from "api/rest/rfp/brokerBidContractApi/types/ContractPricingFrequency";
import usableActions from "utils/store/usableActions";

import { additionalFields, awardedPartnerFields, defaultFields } from "../const";
import { BidRequestFieldType } from "../types";
import createLocationFields from "../utils/createLocationFields";
import createStopsFields from "../utils/createStopsFields";

export const fieldAdapter = createEntityAdapter<BidRequestFieldType>({
  selectId: (field) => field.key,
});

const initialState = fieldAdapter.addMany(
  fieldAdapter.getInitialState({
    stopsCount: 1,
    hidePoNumberTillDispatch: false,
    hideInternalRefTillDispatch: false,
  }),
  [...additionalFields, ...defaultFields]
);

const bidRequestFieldsSliceName = "bidRequestFieldsSlice";

const bidRequestFieldsSlice = createSlice({
  name: bidRequestFieldsSliceName,
  initialState,
  reducers: {
    removeAllStops: (state) => {
      const ids = state.ids.filter((id) => id.toString().startsWith("stop"));

      fieldAdapter.removeMany(state, ids);
    },
    addNStops: (
      state,
      action: PayloadAction<{ stopCount: number; rfpPricingFrequency: ContractPricingFrequency | undefined }>
    ) => {
      const numberOfStops = action.payload.stopCount;
      const pickupCreationOptions =
        action.payload.rfpPricingFrequency === ContractPricingFrequency.BULK_SPOT_QUOTE
          ? { dateRequired: false, dateSelected: true }
          : undefined;
      const stopCreationOptions =
        action.payload.rfpPricingFrequency === ContractPricingFrequency.BULK_SPOT_QUOTE
          ? { dateRequired: false, dateSelected: true }
          : undefined;

      // It's helpful to be aware that "stop" means different things for the user and dev.
      // For dev, we consider "stops" to refer to everything from pick up to dropoff. So inclusive of "pickup".
      // For users, stops refers to everything after "pickup"
      fieldAdapter.addMany(state, createLocationFields("pickup", pickupCreationOptions));
      if (numberOfStops === 1) {
        fieldAdapter.addMany(state, createLocationFields("delivery", stopCreationOptions));
      } else if (numberOfStops > 1) {
        fieldAdapter.addMany(state, createStopsFields(numberOfStops, stopCreationOptions));
      }
    },
    updateOneField: fieldAdapter.updateOne,
    removeOneField: fieldAdapter.removeOne,
    upsertField: fieldAdapter.upsertOne,
    setStops: (
      state,
      action: PayloadAction<{ stopCount: number; rfpPricingFrequency: ContractPricingFrequency | undefined }>
    ) => {
      state.stopsCount = action.payload.stopCount;
      bidRequestFieldsSlice.caseReducers.removeAllStops(state);
      bidRequestFieldsSlice.caseReducers.addNStops(state, bidRequestFieldsSlice.actions.addNStops(action.payload));
    },
    setState: (
      state,
      action: PayloadAction<{
        hidePoNumberTillDispatch?: boolean;
        hideInternalRefTillDispatch?: boolean;
      }>
    ) => {
      if (typeof action.payload.hideInternalRefTillDispatch === "boolean") {
        state.hideInternalRefTillDispatch = action.payload.hideInternalRefTillDispatch;
      }

      if (typeof action.payload.hidePoNumberTillDispatch === "boolean") {
        state.hidePoNumberTillDispatch = action.payload.hidePoNumberTillDispatch;
      }
    },
    setAwardedPartnerFields: (state, action: PayloadAction<boolean>) => {
      fieldAdapter.updateMany(
        state,
        awardedPartnerFields.map((field) => ({
          id: field.key,
          changes: {
            selected: action.payload,
          },
        }))
      );
    },
  },
});

export const {
  useUpdateOneField,
  useSetStops,
  useSetState,
  useRemoveOneField,
  useUpsertField,
  useSetAwardedPartnerFields,
} = usableActions(bidRequestFieldsSlice.actions);
export default bidRequestFieldsSlice;
